/*
 * The widget-heading accordion is not ours: ws-scripts' WidgetHeading renders
 * `data-toggle="collapse"` and relies on Bootstrap 3's collapse plugin, which
 * cms-web only loads per-variation (e.g. oem-lexus, oem-subaru) and never
 * globally. So on most sites we have to load it ourselves.
 *
 * Bootstrap 3 plugins are not idempotent: every execution of collapse.js binds
 * another `click.bs.collapse.data-api` handler on document. With two copies on
 * the page a single click runs the handler twice — the first call opens the
 * panel, the second sees an open, non-transitioning panel and closes it again,
 * so the accordion looks dead. (Variations that also load transition.js survive
 * it: the 350ms animation leaves `transitioning` truthy, so the second call
 * hits an early return. oem-lexus loads collapse.js without transition.js,
 * which is why it breaks there and not elsewhere.)
 *
 * So this module does two things:
 *   1. loads collapse.js — and transition.js, which it needs to animate —
 *      only when the page doesn't already have them, and
 *   2. drops duplicate data-api bindings before a collapse toggle is clicked.
 *
 * Step 2 is what makes this reliable. We cannot tell from here whether cms-web
 * will emit the variation's copy as its own <script> tag or inside a combined
 * bundle, nor whether it will have executed before our async bundle runs — and
 * another widget on the page (ws-packages-options) ships its own copy too. By
 * the time someone clicks, every copy has loaded, so that is the one moment we
 * can count the bindings and know the answer.
 */

export const COLLAPSE_SCRIPT_SRC =
	'/static/dist/v9/media/js/bootstrap/v3.3.7/collapse.min.js';

export const TRANSITION_SCRIPT_SRC =
	'/static/dist/v9/media/js/bootstrap/v3.3.7/transition.min.js';

const DATA_API_EVENT = 'click.bs.collapse.data-api';
const DATA_API_NAMESPACE = 'bs.collapse.data-api';
const COLLAPSE_TOGGLE_SELECTOR = '[data-toggle="collapse"]';

/*
 * What the heading accordion needs, and how to tell whether the page has it.
 * A matching script tag counts even if it hasn't executed yet — no reason to
 * add a copy we know is coming.
 */
const BOOTSTRAP_DEPENDENCIES = [
	{
		src: COLLAPSE_SCRIPT_SRC,
		presentSelector: 'script[src*="bootstrap"][src*="collapse"]',
		isLoaded: () => Boolean(window.jQuery?.fn?.collapse)
	},
	{
		// collapse.js only animates when `$.support.transition` is set, and
		// that comes from transition.js. Without it the panel snaps open like
		// a button while the react-bootstrap panels inside it animate, since
		// those bring their own transition handling. oem-lexus loads collapse
		// without transition; oem-subaru loads the pair.
		src: TRANSITION_SCRIPT_SRC,
		presentSelector: 'script[src*="bootstrap"][src*="transition"]',
		isLoaded: () => Boolean(window.jQuery?.support?.transition)
	}
];

const injectScript = (src) => {
	const script = document.createElement('script');
	script.src = src;
	script.async = true;
	document.body.appendChild(script);
};

/*
 * Returns true once the question is settled and false while we still need to
 * wait for jQuery, since both scripts throw if jQuery isn't defined when they
 * execute. Neither depends on the other at execution time — collapse.js reads
 * `$.support.transition` when a panel is toggled — so async order is fine.
 */
const loadMissingDependencies = () => {
	if (!window.jQuery) {
		return false;
	}
	BOOTSTRAP_DEPENDENCIES.forEach((dependency) => {
		if (
			dependency.isLoaded() ||
			document.querySelector(dependency.presentSelector)
		) {
			return;
		}
		injectScript(dependency.src);
	});
	return true;
};

/*
 * Keep the first data-api binding and unbind the rest, so one click toggles
 * once. Returns how many bindings were dropped. A duplicate binding is never
 * useful to anything on the page, so this is safe for the platform's own
 * accordions as well as ours.
 */
export const dedupeCollapseDataApiHandlers = () => {
	const { jQuery } = window;
	// jQuery._data is internal; if a future jQuery drops it we simply leave the
	// bindings alone rather than guessing.
	if (typeof jQuery?._data !== 'function') {
		return 0;
	}
	const clickHandlers = jQuery._data(document, 'events')?.click;
	if (!clickHandlers) {
		return 0;
	}
	const dataApiHandlers = [...clickHandlers].filter(
		(binding) => binding.namespace === DATA_API_NAMESPACE
	);
	dataApiHandlers.slice(1).forEach((binding) => {
		jQuery(document).off(DATA_API_EVENT, binding.selector, binding.handler);
	});
	return Math.max(dataApiHandlers.length - 1, 0);
};

// Capture phase on document, so this runs before the data-api handlers, which
// jQuery invokes during the bubble phase.
const onCaptureClick = (event) => {
	if (event.target?.closest?.(COLLAPSE_TOGGLE_SELECTOR)) {
		dedupeCollapseDataApiHandlers();
	}
};

// Module scope is shared by every widget instance on the page, so the listener
// is only installed once no matter how many instances render.
let isDedupeInstalled = false;

const installDedupe = () => {
	if (isDedupeInstalled) {
		return;
	}
	isDedupeInstalled = true;
	document.addEventListener('click', onCaptureClick, true);
};

const ensureBootstrapCollapse = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return;
	}

	installDedupe();

	if (!loadMissingDependencies()) {
		// jQuery isn't on the page yet. The plugin is only needed once someone
		// clicks the heading, so it's safe to settle this after page load.
		window.addEventListener('load', loadMissingDependencies, {
			once: true
		});
	}
};

export default ensureBootstrapCollapse;
